import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Settings,
  Mail,
  Shield,
  CreditCard,
  Globe,
  Bell,
  Database,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Users,
  Building2,
  Lock,
  Key,
  Server,
  Activity,
  Star,
  Trash2,
  Plus,
  Search,
  Sparkles,
  DollarSign,
  Clock,
  LayoutGrid,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'general',     label: 'Genel',         icon: Settings   },
  { key: 'email',       label: 'E-posta',        icon: Mail       },
  { key: 'security',    label: 'Güvenlik',       icon: Shield     },
  { key: 'billing',     label: 'Ödeme',          icon: CreditCard },
  { key: 'limits',      label: 'Plan Limitleri', icon: Zap        },
  { key: 'integrations',label: 'Entegrasyonlar', icon: Globe      },
  { key: 'database',    label: 'Veritabanı',     icon: Database   },
  { key: 'featured',    label: 'Öne Çıkan',      icon: Star       },
];

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3.5 border-b border-gray-50 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
);

const SettingField = ({ label, description, children }) => (
  <div className="py-4 border-b border-gray-50 last:border-0">
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0 flex-1">
        <label className="block text-sm font-medium text-gray-800 mb-0.5">{label}</label>
        {description && <p className="text-xs text-gray-400">{description}</p>}
      </div>
      <div className="w-72 shrink-0">{children}</div>
    </div>
  </div>
);

const UltraSettings = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);
  const [showSecret, setShowSecret] = useState({});

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    platform_name: 'Expo Stand',
    platform_url: 'https://expostand.com',
    support_email: 'support@expostand.com',
    timezone: 'Europe/Istanbul',
    language: 'tr',
    maintenance_mode: false,
    registration_enabled: true,
    require_email_verification: true,
  });

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: 'smtp.sendgrid.net',
    smtp_port: '587',
    smtp_user: 'apikey',
    smtp_password: 'SG.xxxx',
    from_name: 'Expo Stand',
    from_email: 'noreply@expostand.com',
    welcome_email: true,
    subscription_email: true,
    invoice_email: true,
    alert_email: true,
  });

  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    max_login_attempts: '5',
    lockout_duration_min: '30',
    session_timeout_hr: '24',
    require_2fa_admin: true,
    ip_whitelist_enabled: false,
    ip_whitelist: '192.168.1.0/24',
    audit_retention_days: '90',
    password_min_length: '8',
  });

  // Plan limit defaults
  const [planLimits, setPlanLimits] = useState({
    trial_days: '14',
    free_rfq: '3',
    basic_rfq: '10',
    pro_rfq: '50',
    enterprise_rfq: '-1',
    basic_storage_gb: '5',
    pro_storage_gb: '25',
    enterprise_storage_gb: '100',
  });

  // Database tab state (hoisted from renderDatabase to comply with React Rules of Hooks)
  const [dbStatus, setDbStatus] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  // Featured Listing state
  const [featuredConfig, setFeaturedConfig] = useState({ is_active: true, price_usd: 299, duration_days: 30, max_slots: 3, description: 'Yüklenici leaderboard\'unda öne çık' });
  const [featuredContractors, setFeaturedContractors] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredSaved, setFeaturedSaved] = useState(false);
  const [featuredError, setFeaturedError] = useState(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);

  const loadFeaturedData = useCallback(async () => {
    if (!token) return;
    setFeaturedLoading(true);
    try {
      const [configRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/ultra/featured-listing-config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ultra/featured-contractors`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setFeaturedConfig(prev => ({ ...prev, ...configRes.data }));
      setFeaturedContractors(contractorsRes.data?.contractors || []);
    } catch (e) {
      setFeaturedError('Veriler yüklenemedi');
    } finally {
      setFeaturedLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'featured') loadFeaturedData();
  }, [activeTab, loadFeaturedData]);

  const saveFeaturedConfig = async () => {
    try {
      await axios.put(`${API}/ultra/featured-listing-config`, { config: featuredConfig }, { headers: { Authorization: `Bearer ${token}` } });
      setFeaturedSaved(true);
      setTimeout(() => setFeaturedSaved(false), 2000);
    } catch (e) {
      setFeaturedError('Kayıt başarısız');
    }
  };

  const handleOrgSearch = async (q) => {
    setOrgSearch(q);
    if (!q || q.length < 2) { setOrgSearchResults([]); return; }
    setOrgSearching(true);
    try {
      const res = await axios.get(`${API}/ultra/organizations?q=${encodeURIComponent(q)}&limit=8`, { headers: { Authorization: `Bearer ${token}` } });
      setOrgSearchResults(res.data?.organizations || []);
    } catch { setOrgSearchResults([]); }
    finally { setOrgSearching(false); }
  };

  const featureOrg = async (orgId, orgName) => {
    try {
      await axios.post(`${API}/ultra/featured-contractors/${orgId}/feature`, { note: 'Admin manuel' }, { headers: { Authorization: `Bearer ${token}` } });
      setOrgSearch(''); setOrgSearchResults([]);
      await loadFeaturedData();
    } catch (e) {
      setFeaturedError(e?.response?.data?.detail || 'Öne çıkarılamadı');
    }
  };

  const unfeatureOrg = async (orgId) => {
    try {
      await axios.delete(`${API}/ultra/featured-contractors/${orgId}/feature`, { headers: { Authorization: `Bearer ${token}` } });
      await loadFeaturedData();
    } catch (e) {
      setFeaturedError(e?.response?.data?.detail || 'Kaldırılamadı');
    }
  };

  const renderFeatured = () => (
    <div className="space-y-6">
      {featuredError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />{featuredError}
          <button onClick={() => setFeaturedError(null)} className="ml-auto text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {/* Fiyatlandırma Konfigürasyonu */}
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-bold text-gray-900">Öne Çıkan Listeleme Konfigürasyonu</h3>
          </div>
          <Button size="sm" onClick={saveFeaturedConfig} className={`gap-1.5 text-xs h-8 ${featuredSaved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}>
            {featuredSaved ? <><CheckCircle2 className="h-3.5 w-3.5" />Kaydedildi</> : <><Save className="h-3.5 w-3.5" />Kaydet</>}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5" />Fiyat (USD)
            </label>
            <input type="number" min="0" value={featuredConfig.price_usd}
              onChange={e => setFeaturedConfig(p => ({ ...p, price_usd: Number(e.target.value) }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />Süre (gün)
            </label>
            <input type="number" min="1" value={featuredConfig.duration_days}
              onChange={e => setFeaturedConfig(p => ({ ...p, duration_days: Number(e.target.value) }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
              <LayoutGrid className="h-3.5 w-3.5" />Maksimum Slot Sayısı
            </label>
            <input type="number" min="1" max="10" value={featuredConfig.max_slots}
              onChange={e => setFeaturedConfig(p => ({ ...p, max_slots: Number(e.target.value) }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block flex items-center gap-1.5">
              <Settings className="h-3.5 w-3.5" />Açıklama
            </label>
            <input type="text" value={featuredConfig.description}
              onChange={e => setFeaturedConfig(p => ({ ...p, description: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Özellik Aktif</p>
              <p className="text-xs text-gray-400">Devre dışı bırakırsan yükleniciler Stripe checkout görmez</p>
            </div>
            <button onClick={() => setFeaturedConfig(p => ({ ...p, is_active: !p.is_active }))}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${featuredConfig.is_active ? 'bg-blue-600' : 'bg-gray-200'}`}>
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${featuredConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Şu an öne çıkan yükleniciler */}
      <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <h3 className="text-sm font-bold text-gray-900">Şu An Öne Çıkan Yükleniciler</h3>
            <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-white">{featuredContractors.length} aktif</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={loadFeaturedData} disabled={featuredLoading} className="h-7 text-xs gap-1">
            <RefreshCw className={`h-3 w-3 ${featuredLoading ? 'animate-spin' : ''}`} />Yenile
          </Button>
        </div>

        {featuredContractors.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <Star className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">Şu an öne çıkan yüklenici yok</p>
          </div>
        ) : (
          <div className="space-y-2">
            {featuredContractors.map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-amber-200">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {(c.display_name || c.name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{c.display_name || c.name}</p>
                  <p className="text-xs text-gray-400">{c.city}{c.city && c.country ? ', ' : ''}{c.country} · Bitiş: {c.featured_until ? new Date(c.featured_until).toLocaleDateString('tr-TR') : '—'}</p>
                  {c.featured_note && <p className="text-xs text-amber-600 italic">{c.featured_note}</p>}
                </div>
                <Button variant="outline" size="sm" onClick={() => unfeatureOrg(c.id || c.org_id)}
                  className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 gap-1 shrink-0">
                  <Trash2 className="h-3 w-3" />Kaldır
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manuel Yüklenici Öne Çıkar */}
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="h-4 w-4 text-blue-500" />
          <h3 className="text-sm font-bold text-gray-900">Manuel Yüklenici Öne Çıkar</h3>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={orgSearch}
            onChange={e => handleOrgSearch(e.target.value)}
            placeholder="Şirket adı ara..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {orgSearching && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />}
        </div>
        {orgSearchResults.length > 0 && (
          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
            {orgSearchResults.map(org => (
              <div key={org.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-800">{org.display_name}</p>
                  <p className="text-xs text-gray-400">{org.city}, {org.country} · {org.role}</p>
                </div>
                <Button size="sm" onClick={() => featureOrg(org.id, org.display_name)}
                  className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1">
                  <Star className="h-3 w-3" />Öne Çıkar
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-gray-400">Seçilen yüklenici {featuredConfig.duration_days} gün boyunca öne çıkarılacak (ücretsiz, manuel olarak).</p>
      </div>
    </div>
  );

  // Integrations state
  const [integrations, setIntegrations] = useState({
    stripe_key: 'sk_live_****',
    stripe_webhook_secret: 'whsec_****',
    stripe_enabled: true,
    sendgrid_key: 'SG.****',
    sendgrid_enabled: true,
    google_oauth_enabled: false,
    google_client_id: '',
    sentry_dsn: '',
    sentry_enabled: false,
  });

  // Load settings from API on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/ultra/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const s = res.data.settings || {};
        if (s.general) setGeneralSettings(prev => ({ ...prev, ...s.general }));
        if (s.email) setEmailSettings(prev => ({ ...prev, ...s.email }));
        if (s.security) setSecuritySettings(prev => ({ ...prev, ...s.security }));
        if (s.limits) setPlanLimits(prev => ({ ...prev, ...s.limits }));
        if (s.integrations) setIntegrations(prev => ({ ...prev, ...s.integrations }));
      })
      .catch(() => { /* use defaults if load fails */ });
  // eslint-disable-next-line
  }, [token]);

  const handleSave = async () => {    try {
      await axios.put(
        `${API}/ultra/settings`,
        {
          settings: {
            general: { ...generalSettings },
            email: { ...emailSettings },
            security: { ...securitySettings },
            limits: { ...planLimits },
            integrations: { ...integrations },
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* ignore — UI feedback is enough */ }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggleSecret = (key) => setShowSecret(p => ({ ...p, [key]: !p[key] }));

  const secretInput = (value, key) => (
    <div className="relative">
      <Input
        type={showSecret[key] ? 'text' : 'password'}
        value={value}
        readOnly
        className="pr-9 bg-gray-50 border-gray-200 font-mono text-xs"
      />
      <button
        type="button"
        onClick={() => toggleSecret(key)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {showSecret[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );

  const renderGeneral = () => (
    <div className="space-y-0 divide-y-0">
      <SettingField label="Platform Adı" description="Kullanıcılara gösterilen platform ismi">
        <Input value={generalSettings.platform_name} onChange={e => setGeneralSettings(p => ({ ...p, platform_name: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="Platform URL" description="Sistem tarafından kullanılan ana URL">
        <Input value={generalSettings.platform_url} onChange={e => setGeneralSettings(p => ({ ...p, platform_url: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="Destek E-postası" description="Kullanıcı iletişimleri için e-posta">
        <Input value={generalSettings.support_email} onChange={e => setGeneralSettings(p => ({ ...p, support_email: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="Saat Dilimi">
        <select value={generalSettings.timezone} onChange={e => setGeneralSettings(p => ({ ...p, timezone: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
          <option value="UTC">UTC</option>
          <option value="Europe/London">Europe/London</option>
          <option value="America/New_York">America/New_York</option>
        </select>
      </SettingField>
      <SettingField label="Varsayılan Dil">
        <select value={generalSettings.language} onChange={e => setGeneralSettings(p => ({ ...p, language: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="tr">Türkçe</option>
          <option value="en">English</option>
          <option value="de">Deutsch</option>
        </select>
      </SettingField>
      <div className="pt-2 space-y-0">
        <ToggleSwitch checked={generalSettings.maintenance_mode} onChange={v => setGeneralSettings(p => ({ ...p, maintenance_mode: v }))} label="Bakım Modu" description="Aktifken sadece adminler giriş yapabilir" />
        <ToggleSwitch checked={generalSettings.registration_enabled} onChange={v => setGeneralSettings(p => ({ ...p, registration_enabled: v }))} label="Kayıt Açık" description="Yeni kullanıcı kaydına izin ver" />
        <ToggleSwitch checked={generalSettings.require_email_verification} onChange={v => setGeneralSettings(p => ({ ...p, require_email_verification: v }))} label="E-posta Doğrulama Zorunlu" description="Kayıt sonrası e-posta onayı gereksin" />
      </div>
    </div>
  );

  const renderEmail = () => (
    <div className="space-y-0">
      <div className="pb-4 mb-2">
        <p className="text-sm font-semibold text-gray-700 mb-0.5">SMTP Konfigürasyonu</p>
        <p className="text-xs text-gray-400">E-posta gönderimi için SMTP sunucu ayarları</p>
      </div>
      <SettingField label="SMTP Host">
        <Input value={emailSettings.smtp_host} onChange={e => setEmailSettings(p => ({ ...p, smtp_host: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="SMTP Port">
        <Input value={emailSettings.smtp_port} onChange={e => setEmailSettings(p => ({ ...p, smtp_port: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="SMTP Kullanıcı">
        <Input value={emailSettings.smtp_user} onChange={e => setEmailSettings(p => ({ ...p, smtp_user: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="SMTP Şifre">
        {secretInput(emailSettings.smtp_password, 'smtp_pw')}
      </SettingField>
      <SettingField label="Gönderen Adı">
        <Input value={emailSettings.from_name} onChange={e => setEmailSettings(p => ({ ...p, from_name: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <SettingField label="Gönderen E-posta">
        <Input value={emailSettings.from_email} onChange={e => setEmailSettings(p => ({ ...p, from_email: e.target.value }))} className="bg-gray-50 border-gray-200" />
      </SettingField>
      <div className="pt-4 space-y-0">
        <p className="text-sm font-semibold text-gray-700 mb-2">Bildirim E-postaları</p>
        <ToggleSwitch checked={emailSettings.welcome_email}      onChange={v => setEmailSettings(p => ({ ...p, welcome_email: v }))}      label="Hoş Geldiniz E-postası"    description="Yeni kayıtta gönderilir" />
        <ToggleSwitch checked={emailSettings.subscription_email} onChange={v => setEmailSettings(p => ({ ...p, subscription_email: v }))} label="Abonelik Bildirimleri"    description="Plan değişikliklerinde gönderilir" />
        <ToggleSwitch checked={emailSettings.invoice_email}      onChange={v => setEmailSettings(p => ({ ...p, invoice_email: v }))}      label="Fatura E-postaları"        description="Ödeme sonrası otomatik fatura" />
        <ToggleSwitch checked={emailSettings.alert_email}        onChange={v => setEmailSettings(p => ({ ...p, alert_email: v }))}        label="Güvenlik Uyarıları"        description="Şüpheli aktivite tespitinde" />
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-0">
      <SettingField label="Maksimum Giriş Denemesi" description="Hesap kilitlenmesi için eşik değeri">
        <Input type="number" value={securitySettings.max_login_attempts} onChange={e => setSecuritySettings(p => ({ ...p, max_login_attempts: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <SettingField label="Kilitleme Süresi (dk)" description="Hesap kilitlendikten sonra bekleme süresi">
        <Input type="number" value={securitySettings.lockout_duration_min} onChange={e => setSecuritySettings(p => ({ ...p, lockout_duration_min: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <SettingField label="Oturum Zaman Aşımı (saat)" description="Hareketsizlik sonrası otomatik çıkış">
        <Input type="number" value={securitySettings.session_timeout_hr} onChange={e => setSecuritySettings(p => ({ ...p, session_timeout_hr: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <SettingField label="Minimum Şifre Uzunluğu">
        <Input type="number" value={securitySettings.password_min_length} onChange={e => setSecuritySettings(p => ({ ...p, password_min_length: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <SettingField label="Denetim Log Saklama (gün)" description="Audit loglarının saklanacağı süre">
        <Input type="number" value={securitySettings.audit_retention_days} onChange={e => setSecuritySettings(p => ({ ...p, audit_retention_days: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <div className="pt-2 space-y-0">
        <ToggleSwitch checked={securitySettings.require_2fa_admin} onChange={v => setSecuritySettings(p => ({ ...p, require_2fa_admin: v }))} label="Admin İçin 2FA Zorunlu" description="Ultra Admin girişinde iki faktörlü doğrulama" />
        <ToggleSwitch checked={securitySettings.ip_whitelist_enabled} onChange={v => setSecuritySettings(p => ({ ...p, ip_whitelist_enabled: v }))} label="IP Beyaz Listesi" description="Sadece izin verilen IP'lerden admin girişine izin ver" />
      </div>
      {securitySettings.ip_whitelist_enabled && (
        <SettingField label="İzin Verilen IP'ler" description="CIDR notasyonu, virgülle ayırın">
          <Input value={securitySettings.ip_whitelist} onChange={e => setSecuritySettings(p => ({ ...p, ip_whitelist: e.target.value }))} className="bg-gray-50 border-gray-200 font-mono text-xs" />
        </SettingField>
      )}
    </div>
  );

  const renderBilling = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Aylık Gelir', value: '₺142,850', sub: 'MRR', icon: Activity, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Aktif Abonelik', value: '324', sub: 'Toplam', icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Başarısız Ödeme', value: '7', sub: 'Müdahale gerekiyor', icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="border-0 bg-gray-50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${s.color}`}><Icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-400">{s.sub}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="border-0 bg-gray-50">
        <CardContent className="p-5">
          <p className="text-sm font-semibold text-gray-800 mb-3">Stripe Konfigürasyonu</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Stripe Bağlantı Durumu</span>
              <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />Bağlı
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Webhook Endpoint</span>
              <code className="text-xs font-mono bg-gray-200 px-2 py-0.5 rounded">/api/stripe/webhook</code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Son Senkronizasyon</span>
              <span className="text-xs text-gray-500">2 dakika önce</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLimits = () => (
    <div className="space-y-0">
      <SettingField label="Deneme Süresi (gün)" description="Yeni abonelikler için ücretsiz deneme">
        <Input type="number" value={planLimits.trial_days} onChange={e => setPlanLimits(p => ({ ...p, trial_days: e.target.value }))} className="bg-gray-50 border-gray-200 w-28" />
      </SettingField>
      <div className="py-4 border-b border-gray-50">
        <p className="text-sm font-semibold text-gray-700 mb-3">RFQ / Teklif Limitleri (aylık)</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            ['Ücretsiz', 'free_rfq'], ['Basic', 'basic_rfq'], ['Professional', 'pro_rfq'], ['Enterprise (-1 = sınırsız)', 'enterprise_rfq']
          ].map(([label, key]) => (
            <div key={key}>
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <Input type="number" value={planLimits[key]} onChange={e => setPlanLimits(p => ({ ...p, [key]: e.target.value }))} className="bg-gray-50 border-gray-200" />
            </div>
          ))}
        </div>
      </div>
      <div className="py-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">Depolama Limitleri (GB)</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Basic', 'basic_storage_gb'], ['Pro', 'pro_storage_gb'], ['Enterprise', 'enterprise_storage_gb']
          ].map(([label, key]) => (
            <div key={key}>
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <Input type="number" value={planLimits[key]} onChange={e => setPlanLimits(p => ({ ...p, [key]: e.target.value }))} className="bg-gray-50 border-gray-200" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderIntegrations = () => (
    <div className="space-y-4">
      {[
        {
          name: 'Stripe', icon: CreditCard, enabled: integrations.stripe_enabled,
          toggle: v => setIntegrations(p => ({ ...p, stripe_enabled: v })),
          fields: [
            { label: 'API Anahtarı (Secret)', value: integrations.stripe_key, secret: true, key: 'stripe_key' },
            { label: 'Webhook Secret', value: integrations.stripe_webhook_secret, secret: true, key: 'stripe_ws' },
          ]
        },
        {
          name: 'SendGrid', icon: Mail, enabled: integrations.sendgrid_enabled,
          toggle: v => setIntegrations(p => ({ ...p, sendgrid_enabled: v })),
          fields: [
            { label: 'API Anahtarı', value: integrations.sendgrid_key, secret: true, key: 'sg_key' },
          ]
        },
        {
          name: 'Google OAuth', icon: Globe, enabled: integrations.google_oauth_enabled,
          toggle: v => setIntegrations(p => ({ ...p, google_oauth_enabled: v })),
          fields: [
            { label: 'Client ID', value: integrations.google_client_id, secret: false },
          ]
        },
        {
          name: 'Sentry', icon: Activity, enabled: integrations.sentry_enabled,
          toggle: v => setIntegrations(p => ({ ...p, sentry_enabled: v })),
          fields: [
            { label: 'DSN', value: integrations.sentry_dsn, secret: false },
          ]
        },
      ].map(integration => {
        const Icon = integration.icon;
        return (
          <Card key={integration.name} className="border border-gray-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-gray-100 rounded-lg"><Icon className="h-5 w-5 text-gray-600" /></div>
                  <div>
                    <p className="font-semibold text-gray-900">{integration.name}</p>
                    <Badge className={integration.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                      {integration.enabled ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </div>
                </div>
                <button
                  onClick={() => integration.toggle(!integration.enabled)}
                  className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${integration.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${integration.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {integration.enabled && (
                <div className="space-y-3">
                  {integration.fields.map(f => (
                    <div key={f.label}>
                      <p className="text-xs font-medium text-gray-500 mb-1">{f.label}</p>
                      {f.secret ? secretInput(f.value, f.key) : (
                        <Input value={f.value} placeholder="Değer girin..." className="bg-gray-50 border-gray-200" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderDatabase = () => {
    const runCleanup = async () => {
      setCleaning(true);
      setDbStatus(null);
      try {
        const res = await axios.post(`${API}/admin/cleanup-duplicate-briefs`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setDbStatus({ type: 'success', message: `Temizlendi: ${res.data.deleted} kayıt silindi (${res.data.duplicate_groups} duplike grup)` });
      } catch (err) {
        setDbStatus({ type: 'error', message: err?.response?.data?.detail || 'Temizleme başarısız' });
      } finally { setCleaning(false); }
    };

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
          <h3 className="text-sm font-bold text-orange-800 flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4" />Dikkat: Bu işlemler geri alınamaz</h3>
          <p className="text-xs text-orange-600">Aşağıdaki işlemler gerçek veritabanı kayıtlarını etkiler. Dikkatli kullanın.</p>
        </div>

        {/* Cleanup briefs */}
        <div className="border border-gray-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Duplike Brief Temizliği</h3>
              <p className="text-xs text-gray-500 mt-0.5">Aynı fuar adına sahip mükerrer brief kayıtlarını siler. Her fuar için en son kayıt tutulur, geri kalanlar silinir.</p>
            </div>
            <Button
              onClick={runCleanup}
              disabled={cleaning}
              data-testid="cleanup-duplicate-briefs-btn"
              variant="outline"
              size="sm"
              className="shrink-0 border-orange-300 text-orange-700 hover:bg-orange-50 gap-2"
            >
              {cleaning ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" />Temizleniyor...</> : <><Database className="h-3.5 w-3.5" />Temizle</>}
            </Button>
          </div>
          {dbStatus && (
            <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${dbStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {dbStatus.message}
            </div>
          )}
        </div>

        {/* DB Stats */}
        <div className="border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Veritabanı İstatistikleri</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Koleksiyonlar', value: '12', icon: Database },
              { label: 'İndeksler', value: 'Otomatik', icon: Zap },
              { label: 'Motor', value: 'MongoDB', icon: Server },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                <s.icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className="text-sm font-bold text-gray-800">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const contentMap = {
    general:      renderGeneral,
    email:        renderEmail,
    security:     renderSecurity,
    billing:      renderBilling,
    limits:       renderLimits,
    integrations: renderIntegrations,
    database:     renderDatabase,
    featured:     renderFeatured,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sistem Ayarları</h1>
          <p className="text-sm text-gray-500 mt-0.5">Platform geneli konfigürasyon ve entegrasyon ayarları</p>
        </div>
        <Button onClick={handleSave} className={`gap-2 ${saved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}>
          {saved ? <><CheckCircle2 className="h-4 w-4" />Kaydedildi</> : <><Save className="h-4 w-4" />Kaydet</>}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Nav */}
        <div className="w-52 shrink-0">
          <nav className="space-y-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    activeTab === tab.key
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <Card className="flex-1 border-0 shadow-sm">
          <CardHeader className="pb-4 border-b border-gray-100">
            <CardTitle className="text-base flex items-center gap-2">
              {(() => {
                const t = TABS.find(t => t.key === activeTab);
                const Icon = t?.icon || Settings;
                return <><Icon className="h-5 w-5 text-blue-600" />{t?.label}</>;
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {contentMap[activeTab]?.()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UltraSettings;
